// Palworld product-release cluster (2026): the second TCG booster set,
// accessory products, and the official merchandise lines (apparel, plush,
// figures, collectables, mystery/blind boxes).
//
// Structure mirrors src/lib/guides-tcg-longtail.ts so each topic expands into
// exactly five AEO-shaped articles (overview, step-by-step, reference,
// mistakes, FAQ). Every claim here is limited to what the official product
// pages state; nothing about pull rates, print runs or resale value is invented.

import type { Guide, GuideArticle } from "./guides-data";
import { OFFICIAL_LINKS } from "./official-links";

interface Topic {
  slug: string;
  title: string;
  desc: string;
  keywords: string;
  hub: string;
  hubLabel: string;
  tag: string;
  question: string;
  answer: string;
  steps: [string, string][];
  data: [string, string][];
  pitfalls: [string, string][];
  faq: [string, string][];
}

const TOPICS: Topic[] = [
  {
    slug: "palworld-tcg-legends-awaken-bp02",
    title: "Palworld TCG Legends Awaken (BP02)",
    desc: "Legends Awaken release date, official Jetragon reveal, card-list status, 100-card set details and verified Palworld TCG BP02 updates.",
    keywords: "palworld tcg legends awaken, palworld bp02, palworld card game second set, palworld legends awaken card list, jetragon legends awaken card, jetragon legend palworld tcg, frostallion legend palworld tcg, palworld tcg new set 2026, palworld bp02 booster box",
    hub: "/cards",
    hubLabel: "Palworld card database",
    tag: "TCG Set",
    question: "What is Palworld TCG Legends Awaken (BP02)?",
    answer:
      "Legends Awaken is the second Palworld OFFICIAL CARD GAME booster, releasing October 30, 2026 with 100 normal card types plus parallels across five colors. Jetragon is officially confirmed as a featured Legendary Pal, but Bushiroad has not published an English card code, rules text, rarity or individual scan for a card named Jetragon Legend. Frostallion Legend is also not present in the official card list, so Pal Stars will not invent either record.",
    steps: [
      [
        "Confirm the set code before you buy singles",
        "English Legends Awaken cards read EBP02 followed by a dash and the collector number, the same pattern as EBP01-001. Any listing selling \"BP02\" cards with a different prefix is not an English official printing.",
      ],
      [
        "Pre-order sealed from an official retailer",
        "Bushiroad opened Legends Awaken pre-orders alongside Sleeve & Card Set Vol. 1. Use the official retailer locator rather than a marketplace listing so you pay list price on release rather than pre-release resale price.",
      ],
      [
        "Keep your BP01 deck legal in the meantime",
        "Nothing in BP01 rotates when BP02 lands. Build and validate your current 50-card list in the deck builder now, then slot BP02 cards into the same shell instead of rebuilding.",
      ],
      [
        "Watch the official card list page, not resellers",
        "The card list for a set publishes on the official site after the product announcement, exactly as Dawn of Palpagos did on 9 July 2026. Pal Stars re-checks that list daily and indexes new printed numbers as soon as they appear.",
      ],
      [
        "Price-check on release week, not pre-release",
        "Pre-release singles prices are speculation. Track the first real sold prices in the price hub once packs are actually open.",
      ],
    ],
    data: [
      ["Set name", "Booster Pack \"Legends Awaken\" — the second Palworld OFFICIAL CARD GAME booster."],
      ["Set code", "BP02, printed on English cards as EBP02 followed by the collector number."],
      ["Announced", "12 June 2026, with pre-orders for the booster and Sleeve & Card Set Vol. 1 opening the same day."],
        ["Pals shown at announcement", "Broncherry and Jetragon were shown in official announcement material; Legendary Pals are a stated theme."],
        ["Release date and size", "October 30, 2026; 100 normal card types (RR, R, U and C) plus parallels across Red, Blue, Green, Purple and Colorless."],
        ["Jetragon Legend status", "Jetragon is confirmed for the set, but no official Jetragon Legend title, printed code, rarity, effect or card scan is published yet."],
        ["Frostallion Legend status", "No official Frostallion Legend card record or scan is published yet. Pal Stars will add it only after the official card list confirms it."],
      ["Predecessor", "BP01 Dawn of Palpagos, released 30 July 2026, whose card list published on 9 July 2026."],
      ["Card list status", "Not yet published on the official card list at the time of writing; Pal Stars indexes new EBP02 numbers automatically once it is."],
    ],
    pitfalls: [
      [
        "Buying \"BP02 singles\" before the card list exists",
        "If the official card list has no EBP02 numbers yet, a single cannot be verified. Wait for a printed number you can check in the card checker.",
      ],
      [
        "Assuming BP01 cards stop being legal",
        "The Palworld card game has no announced rotation. Your Dawn of Palpagos cards remain playable when Legends Awaken releases.",
      ],
      [
        "Trusting fan-made pull-rate charts for a new set",
        "Pull rates are only measurable once a large number of real packs are open. Treat pre-release rate claims as guesses.",
      ],
      [
        "Paying pre-release prices for sealed",
        "Pre-order stock from official retailers sells at list price. Marketplace pre-release listings routinely carry a large markup.",
      ],
    ],
    faq: [
      ["When was Palworld TCG Legends Awaken announced?", "Bushiroad announced Booster Pack \"Legends Awaken\" on 12 June 2026, opening pre-orders for the booster and Sleeve & Card Set Vol. 1 the same day."],
      ["What is the set code for Legends Awaken?", "BP02. English cards print it as EBP02 followed by the collector number, matching the EBP01 pattern used by Dawn of Palpagos."],
      ["Which new Pals are in Palworld Legends Awaken?", "Broncherry and Jetragon were shown in official material, and Bushiroad says Legendary Pals feature in the set. The complete list is confirmed only when the official card list publishes."],
      ["Is Jetragon Legend an official Palworld TCG card?", "Jetragon is officially featured in Legends Awaken, but no official English card named Jetragon Legend—with a printed code, effect, rarity and individual scan—has been published yet."],
      ["Is Frostallion Legend in Legends Awaken?", "Not yet confirmed by the official card list. Pal Stars does not add fan-made or unverified Frostallion Legend records to the database."],
      ["Is Legends Awaken the second Palworld card set?", "Yes. It is the second booster pack, following BP01 Dawn of Palpagos which released on 30 July 2026."],
      ["Do BP01 cards work with BP02 cards?", "Yes. Both sets are part of the same card pool and no rotation has been announced, so you can mix them in one 50-card deck."],
    ],
  },
  {
    slug: "palworld-tcg-sleeve-and-card-set-vol-1",
    title: "Palworld TCG Sleeve & Card Set Vol. 1",
    desc: "What Palworld OFFICIAL CARD GAME Sleeve & Card Set Vol. 1 contains, how sleeves are checked at sanctioned events, and when an accessory product is worth pre-ordering.",
    keywords: "palworld tcg sleeve and card set vol 1, palworld card sleeves official, palworld tcg accessories, palworld card game deck sleeves, palworld tcg playmat sleeves",
    hub: "/tcg-tools",
    hubLabel: "Palworld TCG tools",
    tag: "TCG Product",
    question: "What is Palworld TCG Sleeve & Card Set Vol. 1?",
    answer:
      "Sleeve & Card Set Vol. 1 is an official Palworld OFFICIAL CARD GAME accessory product that bundles branded deck sleeves with card content, announced with Legends Awaken pre-orders on 12 June 2026. Official sleeves matter because sanctioned events require every card in a deck to be in identical, opaque-backed sleeves.",
    steps: [
      ["Buy one set per deck, not one per player", "A 50-card main deck plus a 10-card Soul deck needs 60 sleeves before you count spares. Check the sleeve count on the product page against that number."],
      ["Sleeve the whole deck the same way", "Every card must sit in the same sleeve design, facing the same direction. Mixed sleeves are the most common deck check failure at shop events."],
      ["Keep 3–5 spare sleeves in your bag", "A split sleeve mid-round means a marked card. Judges accept a swap only if you have an identical spare."],
      ["Double-sleeve only your chase parallels", "Inner sleeves protect OSR and SSP printings, but a double-sleeved deck must be double-sleeved throughout or the thickness gives cards away."],
      ["Store the bundled cards unsleeved and flat", "Card content that ships in an accessory product is usually promo-stamped. Keep it flat in a top-loader until you decide whether to play it."],
    ],
    data: [
      ["Product", "Palworld OFFICIAL CARD GAME Sleeve & Card Set Vol. 1."],
      ["Announced", "12 June 2026, in the same news post as Legends Awaken pre-orders."],
      ["Why official sleeves", "Sanctioned play requires uniform, opaque, unmarked sleeve backs across the entire deck."],
      ["Sleeves needed per deck", "60 minimum — 50 main deck plus 10 Soul deck — before spares."],
      ["Legality note", "Sleeves never change a card's legality; only the printed card number and copy limits do."],
    ],
    pitfalls: [
      ["Sleeving only the expensive cards", "A partially sleeved deck is a marked deck. Sleeve everything or nothing."],
      ["Using scuffed sleeves in a tournament", "Visible scuffs on specific sleeves let an opponent identify cards from the back and can cost you the match."],
      ["Buying a single pack of sleeves for a full deck", "Count the sleeves against 60 before ordering; one small pack rarely covers a legal deck."],
      ["Playing the bundled promo without checking legality", "Confirm the printed number in the card checker first — some promo printings are for collecting rather than a specific format."],
    ],
    faq: [
      ["What comes in Palworld Sleeve & Card Set Vol. 1?", "It is an official accessory bundle pairing branded Palworld card sleeves with card content, announced on 12 June 2026 alongside Legends Awaken pre-orders."],
      ["Do I need official sleeves to play the Palworld card game?", "No brand is mandated, but sanctioned events require all sleeves in your deck to be identical, opaque and unmarked."],
      ["How many sleeves does a Palworld deck need?", "At least 60 — 50 for the main deck and 10 for the Soul deck — plus a few spares for splits."],
      ["Are sleeve-set promo cards tournament legal?", "Check the printed card number in the card checker; legality follows the card number and copy limit, never the packaging."],
    ],
  },
  {
    slug: "palworld-official-merchandise",
    title: "Palworld Official Merchandise",
    desc: "Where official Palworld merchandise actually comes from — apparel, plush, figures, collectables and mystery boxes — and how to tell a licensed product from a knock-off.",
    keywords: "palworld official merchandise, palworld merch store, palworld shirts, palworld hoodie, palworld plushies, palworld figures, palworld collectables, palworld official store",
    hub: "/prices",
    hubLabel: "Pal Stars price hub",
    tag: "Merchandise",
    question: "Where can I buy official Palworld merchandise?",
    answer:
      "Official Palworld merchandise is licensed by Pocketpair and sold through named partners rather than one single storefront: apparel and general goods through Pocketpair's official channels, figures and blind-box collectables through The Loyal Subjects, and TCG-side collectables and figure boxes through Bushiroad. Always start from a link on palworld.com or pocketpair.jp — a licensed product names its licensor on the packaging.",
    steps: [
      ["Start at the official site, not a search ad", "Open palworld.com or pocketpair.jp and follow their merchandise links. Search ads for \"palworld merch\" are dominated by print-on-demand shops with no licence."],
      ["Check the licensor line on the product page", "A licensed item credits Pocketpair (and for card-game goods, Bushiroad). No licensor credit means no licence."],
      ["Match the product line to what you want", "Apparel such as shirts and hoodies, plush in the 7–8 inch small-plush line, 3–4 inch figures and 3-packs, micro figures in egg-style blind boxes, and Bushiroad's Palworld Collection Figure boxes are separate lines with separate release dates."],
      ["Read pre-order terms before you commit", "Collectable pre-orders commonly ship only when every item on the order is in stock, so mixing an in-stock item with a pre-order delays both. Place separate orders."],
      ["Verify sizing and material for apparel", "Licensed apparel lists fabric weight and a size chart. Vague or missing sizing is a strong signal of an unlicensed drop-ship listing."],
    ],
    data: [
      ["Licensor", "Pocketpair licenses Palworld merchandise; Bushiroad handles the card game and card-game-adjacent collectables."],
      ["Apparel", "Shirts and hoodies — check the licensor credit and a published size chart before ordering."],
      ["Plush", "The Loyal Subjects' Palworld small plush line is 7–8 inches, sold individually and as display sets."],
      ["Figures", "3–4 inch figures, including a 3-inch Collectors Set 3-pack."],
      ["Blind boxes", "Micro Pal figures sold in egg-style incubator blind boxes with rarer chase figures."],
      ["Bushiroad collectables", "Palworld Collection Figure Vol. 1 boxes contain multiple packs across a set of Pal designs."],
      ["Fan art vs licensed", "Fan-made merch is legal to sell in many places but is not official; only licensed goods credit Pocketpair."],
    ],
    pitfalls: [
      ["Assuming any store selling Palworld art is official", "Print-on-demand marketplaces list unlicensed Palworld apparel constantly. No licensor credit, no licence."],
      ["Mixing pre-orders with in-stock items", "Most collectable retailers hold the whole order until the last pre-order arrives. Split the order."],
      ["Buying blind boxes to chase one specific Pal", "Blind boxes are random by design. If you want a specific Pal, buy the named single figure instead."],
      ["Treating merchandise as an investment", "Mass-produced plush and apparel rarely appreciate. Buy it because you want it, not as a trade."],
      ["Paying resale for an item still on pre-order", "Check the official product page first — resale listings for pre-order stock routinely double the list price."],
    ],
    faq: [
      ["Is there an official Palworld merchandise store?", "Palworld merchandise is licensed by Pocketpair and sold through named partners rather than one storefront. Start from the merchandise links on palworld.com or pocketpair.jp so you land on a licensed retailer."],
      ["Are Palworld plushies official?", "The Loyal Subjects' 7–8 inch Palworld small plush line is a licensed product. Unlicensed plush exists on marketplaces; check for the Pocketpair licence credit."],
      ["What Palworld figures are available?", "Licensed lines include 3–4 inch single figures, a 3-inch Collectors Set 3-pack, micro figures in egg-style blind boxes, and Bushiroad's Palworld Collection Figure Vol. 1 boxes."],
      ["Are Palworld shirts and hoodies official?", "Only when the listing credits Pocketpair as licensor and publishes real sizing. Most \"Palworld shirt\" search results are unlicensed print-on-demand."],
      ["How do I spot fake Palworld merchandise?", "Look for a licensor credit on the packaging or product page, an official retailer link, published sizing or figure dimensions, and a stated release date. Missing all four means it is almost certainly unlicensed."],
    ],
  },
  {
    slug: "palworld-plush-and-figures",
    title: "Palworld Plush and Figures",
    desc: "The licensed Palworld plush and figure lines in plain terms: sizes, sets, release windows and which line to buy if you want one specific Pal.",
    keywords: "palworld plush, palworld plushies, palworld figures, palworld collection figure vol 1, palworld micro figures, palworld collectors set, palworld toys",
    hub: "/prices",
    hubLabel: "Pal Stars price hub",
    tag: "Merchandise",
    question: "What official Palworld plush and figures are there?",
    answer:
      "Licensed Palworld plush and figures split into four lines: The Loyal Subjects' 7–8 inch small plush (sold singly and as display sets), 3–4 inch single figures, a 3-inch Collectors Set 3-pack, and micro figures packaged in egg-style blind boxes. Bushiroad separately produces the Palworld Collection Figure Vol. 1 box, whose packs cover a set of Pal designs.",
    steps: [
      ["Decide named single or random blind box first", "A named figure guarantees the Pal you want. A blind box is cheaper per figure but random, with rarer chase figures."],
      ["Check the size before you compare prices", "A 7–8 inch plush, a 3–4 inch figure and a micro figure are different products at different price points. Compare like with like."],
      ["Buy display sets for completion, singles for one Pal", "PDQ display sets exist to complete a plush series. If you want one Pal, the single costs far less."],
      ["Confirm the ship window", "Collectable lines list ship quarters rather than dates. Read the quarter on the page and treat any earlier promise as unreliable."],
      ["Keep packaging if you care about resale", "For figures and blind-box chases, an intact sealed box is most of the resale value."],
    ],
    data: [
      ["Small plush", "The Loyal Subjects Palworld small plush, 7–8 inches, Series 1, available singly and as a complete PDQ display set."],
      ["Single figures", "3–4 inch scale, sold as named individual Pals."],
      ["Collectors Set", "3-inch Palworld Collectors Set, a 3-pack."],
      ["Micro figures", "Half-scale micro figures packaged in an egg-style incubator blind box, including rarer chase figures."],
      ["Bushiroad figure box", "Palworld Collection Figure Vol. 1 — a box containing multiple packs across a set of Pal designs, PVC construction."],
      ["Ship windows", "Announced collectable lines listed Q3 2026 ship windows at time of writing."],
    ],
    pitfalls: [
      ["Buying a blind box for a specific Pal", "Blind boxes are random. Chasing one Pal through blind boxes usually costs more than the named single figure."],
      ["Comparing a plush price to a micro figure price", "Different lines, different scales. Compare within a line."],
      ["Ignoring the split-order rule on pre-orders", "One pre-order item can hold an entire order. Order pre-orders separately from in-stock items."],
      ["Opening a sealed collectable you intend to trade", "Sealed condition carries most of the resale premium on figures and blind boxes."],
    ],
    faq: [
      ["How big are the official Palworld plushies?", "The Loyal Subjects Series 1 Palworld small plush line is 7–8 inches tall, sold individually and as a complete display set."],
      ["What is the Palworld Collection Figure Vol. 1?", "A Bushiroad figure box containing multiple packs that cover a set of Pal designs in PVC, listed for a 31 July 2026 availability date."],
      ["Are Palworld micro figures random?", "Yes. Micro Pal figures ship in egg-style blind boxes, so the specific Pal — including rarer chase figures — is random."],
      ["Where do official Palworld figures ship from?", "They are sold through licensed collectable retailers and general listings for those retailers' products; start from an official Palworld merchandise link to confirm the licence."],
    ],
  },
  {
    slug: "palworld-mystery-boxes-and-blind-boxes",
    title: "Palworld Mystery Boxes and Blind Boxes",
    desc: "How Palworld mystery boxes, egg blind boxes and mystery-box card lots actually work, what the odds mean, and when a mystery box is a bad deal.",
    keywords: "palworld mystery box, palworld blind box, palworld mystery eggs, palworld egg reveal figure, palworld tcg mystery box, palworld chase figure odds",
    hub: "/booster-pack",
    hubLabel: "Pal Stars booster pack simulator",
    tag: "Merchandise",
    question: "Are Palworld mystery boxes worth buying?",
    answer:
      "A Palworld mystery box is a randomised product: licensed examples include the CheeBee Micro Pal egg incubator blind boxes with rare chase figures, and Bushiroad figure boxes whose packs cover a design set. They are good value when you want any figure from the line and poor value when you want one specific Pal, because you cannot choose the contents. Third-party \"mystery card lots\" are not an official product at all and carry no stated odds.",
    steps: [
      ["Separate licensed blind boxes from reseller mystery lots", "Licensed blind boxes state the line, the design set and that chases exist. A reseller mystery card lot states nothing verifiable."],
      ["Work out cost per figure you actually want", "Divide the box price by the number of designs you would keep. If that number is one, buy the named single instead."],
      ["Read the chase wording carefully", "Official pages describe chase figures as rare without publishing a rate. Treat any specific percentage from a third party as unverified."],
      ["Use the free simulator for pack curiosity", "If you want the opening experience rather than a specific card, the Pal Stars booster pack simulator is free and does not risk money."],
      ["Check sealed-product prices before buying a lot", "For cards specifically, compare a mystery lot price against sealed pack and single prices in the price hub. Lots priced above sealed are rarely worth it."],
    ],
    data: [
      ["Licensed blind box", "CheeBee Micro Pal egg incubator — micro figure plus accessory, with rare chase figures, random contents."],
      ["Licensed figure box", "Palworld Collection Figure Vol. 1 — a box of multiple packs covering a set of Pal designs."],
      ["Published odds", "Official pages describe chases as rare; no official pull rate is published for blind-box figures."],
      ["TCG mystery lots", "Not an official product. No licensor, no stated odds, no guarantee of card condition."],
      ["Better alternative for one Pal", "A named single figure or a specific card single, bought outright."],
      ["Free alternative for the opening feel", "The Pal Stars booster pack simulator with daily free packs, no money involved."],
    ],
    pitfalls: [
      ["Believing a third-party \"guaranteed chase\" claim", "If odds are not published by the licensor, nobody selling the box can guarantee a chase."],
      ["Buying mystery card lots above sealed price", "A sealed pack has known contents-by-set. A lot does not. Paying more for less information is the worst trade in the hobby."],
      ["Repeat-buying to complete a set", "Randomised products have no completion guarantee. Set completion is almost always cheaper through singles."],
      ["Confusing the Pal Stars simulator with a paid loot box", "The Pal Stars pack game and Palsino are free, use no real money and pay out nothing of monetary value."],
    ],
    faq: [
      ["What is in a Palworld mystery box?", "Licensed Palworld mystery products contain randomised figures — for example a micro Pal figure and accessory in an egg-style incubator blind box, with rarer chase figures possible."],
      ["Are Palworld blind box odds published?", "No official pull rate is published. Official pages describe chase figures as rare, so treat any specific percentage from a reseller as unverified."],
      ["Is a Palworld TCG mystery box official?", "No. Mystery card lots are assembled by resellers, not by Bushiroad, and come with no stated odds or condition guarantee."],
      ["What is the cheapest way to get one specific Pal figure?", "Buy the named single figure. Blind boxes are random, so chasing one Pal through them usually costs more."],
      ["Is there a free way to try Palworld pack opening?", "Yes — the Pal Stars booster pack simulator opens real card-list packs for free with daily free packs and no money involved."],
    ],
  },
];

const POOL = OFFICIAL_LINKS;
function pool(offset: number) {
  return [POOL[offset % POOL.length], POOL[(offset + 1) % POOL.length], POOL[(offset + 2) % POOL.length]];
}

function articles(t: Topic): GuideArticle[] {
  const hubSentence = `Everything here pairs with the ${t.hubLabel} at ${t.hub}.`;
  return [
    {
      title: t.title,
      slug: "overview",
      intro: `${t.answer} ${hubSentence}`,
      sections: [
        { heading: t.question, text: t.answer },
        { heading: "Why this matters", text: `${t.steps[0][1]} ${t.pitfalls[0][1]}` },
        { heading: "What is actually confirmed", text: t.data.map(([k, v]) => `${k}: ${v}`).join(" ") },
        {
          heading: `Use it with the ${t.hubLabel}`,
          text: `${hubSentence} Open ${t.hub} and work from the live data there rather than a remembered price or release date.`,
        },
      ],
      externalLinks: pool(2),
    },
    {
      title: `${t.title}: step-by-step`,
      slug: "step-by-step",
      intro: "The order to work through, with the mistake each step removes.",
      sections: t.steps.map(([heading, text], i) => ({ heading: `${i + 1}. ${heading}`, text })),
      externalLinks: pool(0),
    },
    {
      title: `${t.title}: reference table`,
      slug: "reference",
      intro: `Confirmed details behind ${t.title.toLowerCase()}, in the order you will need them.`,
      sections: t.data.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(1),
    },
    {
      title: `${t.title}: mistakes and fixes`,
      slug: "mistakes",
      intro: "The failure modes that cost buyers the most money on this topic, each with the fix.",
      sections: t.pitfalls.map(([heading, text]) => ({ heading, text })),
      externalLinks: pool(3),
    },
    {
      title: `${t.title}: FAQ`,
      slug: "faq",
      intro: `Direct answers to the questions people ask about ${t.title.toLowerCase()}.`,
      sections: t.faq.map(([q, a]) => ({ heading: q, text: a })),
      externalLinks: pool(2),
    },
  ];
}

export const PRODUCT_2026_GUIDES: Guide[] = TOPICS.map((t) => ({
  slug: t.slug,
  title: `${t.title} Guide & FAQ`,
  desc: t.desc,
  tag: t.tag,
  updated: "2026-08-26",
  keywords: t.keywords,
  articles: articles(t),
}));

/** Hub page each product cluster supports, for internal-link rendering. */
export const PRODUCT_CLUSTERS: Record<string, { slug: string; title: string }[]> = TOPICS.reduce(
  (acc, t) => {
    (acc[t.hub] ??= []).push({ slug: t.slug, title: t.title });
    return acc;
  },
  {} as Record<string, { slug: string; title: string }[]>,
);
